import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Clock, AlertTriangle, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for access status to prevent duplicate API calls
const accessStatusCache = {
  data: null,
  timestamp: null,
  isValid: function() {
    return this.data && this.timestamp && (Date.now() - this.timestamp < 60000); // 1 minute cache
  }
};

/**
 * Founder Trial Badge Component
 * Shows trial days remaining for Founder Circle members on their dashboard
 */
export function FounderTrialBadge({ className }) {
  const navigate = useNavigate();
  const [trialInfo, setTrialInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    fetchTrialInfo();
  }, []);

  const fetchTrialInfo = async () => {
    try {
      let data;
      
      // Use cached data if available
      if (accessStatusCache.isValid()) {
        data = accessStatusCache.data;
      } else {
        const response = await fetch(`${API_URL}/api/access/status`, {
          credentials: 'include'
        });

        if (!response.ok) {
          setShouldRender(false);
          return;
        }
        
        data = await response.json();
        accessStatusCache.data = data;
        accessStatusCache.timestamp = Date.now();
      }
      
      // Only show badge if user is explicitly a founder in trial status with valid days remaining
      // Non-founders (is_founder === false) should NEVER see this component
      if (data.is_founder === true && data.founder_status !== 'paid' && data.founder_days_remaining !== null && data.founder_days_remaining !== undefined) {
        setTrialInfo({
          daysRemaining: data.founder_days_remaining,
          expiresAt: data.founder_expires_at,
          status: data.founder_status
        });
        // Add delay to prevent flash during page transitions
        setTimeout(() => setShouldRender(true), 300);
      } else {
        // Not a founder or paid founder - never render
        setShouldRender(false);
      }
    } catch (error) {
      console.error('Error fetching trial info:', error);
      // On error, don't show the badge
      setShouldRender(false);
    } finally {
      setLoading(false);
      setHasCheckedAuth(true);
    }
  };

  // CRITICAL: Don't render ANYTHING until:
  // 1. Auth check is complete (hasCheckedAuth)
  // 2. User is confirmed to be a founder with trial info (trialInfo exists)
  // 3. Rendering is explicitly enabled (shouldRender)
  // 4. User hasn't dismissed the banner
  // If ANY of these conditions fail, return null immediately
  if (loading || !hasCheckedAuth || !shouldRender || !trialInfo || dismissed) return null;

  const { daysRemaining } = trialInfo;
  
  // Don't show if paid founder
  if (trialInfo.status === 'paid') return null;

  // Determine urgency level
  const getUrgencyLevel = () => {
    if (daysRemaining <= 0) return 'expired';
    if (daysRemaining <= 7) return 'critical';
    if (daysRemaining <= 14) return 'warning';
    return 'normal';
  };

  const urgency = getUrgencyLevel();

  const getStyles = () => {
    switch (urgency) {
      case 'expired':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-600',
          text: 'text-white',
          border: 'border-red-400',
          icon: AlertTriangle,
          iconClass: 'text-white animate-pulse'
        };
      case 'critical':
        return {
          bg: 'bg-gradient-to-r from-orange-500 to-red-500',
          text: 'text-white',
          border: 'border-orange-400',
          icon: AlertTriangle,
          iconClass: 'text-white animate-pulse'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-400 to-orange-400',
          text: 'text-white',
          border: 'border-amber-300',
          icon: Clock,
          iconClass: 'text-white'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-amber-50 to-orange-50',
          text: 'text-amber-900',
          border: 'border-amber-200',
          icon: Crown,
          iconClass: 'text-amber-600'
        };
    }
  };

  const styles = getStyles();
  const IconComponent = styles.icon;

  const getMessage = () => {
    if (daysRemaining <= 0) {
      return "Your Founder trial has ended. Upgrade now to keep your premium access!";
    }
    if (daysRemaining === 1) {
      return "Last day of your Founder trial! Upgrade now to keep your premium access.";
    }
    if (daysRemaining <= 7) {
      return `Only ${daysRemaining} days left in your Founder trial! Lock in your special pricing.`;
    }
    if (daysRemaining <= 14) {
      return `${daysRemaining} days remaining in your Founder Circle trial.`;
    }
    return `${daysRemaining} days remaining in your Founder Circle trial. Enjoy full premium access!`;
  };

  return (
    <div 
      data-testid="founder-trial-badge"
      className={cn(
        'rounded-xl p-4 mb-6 relative overflow-hidden',
        styles.bg,
        styles.border,
        'border',
        className
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            urgency === 'normal' ? 'bg-amber-100' : 'bg-white/20'
          )}>
            <Crown className={cn('w-5 h-5', styles.iconClass)} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn('font-semibold text-sm', styles.text)}>
                Founder Circle Trial
              </span>
              <Badge 
                className={cn(
                  'text-xs',
                  urgency === 'normal' 
                    ? 'bg-amber-200 text-amber-800' 
                    : 'bg-white/30 text-white'
                )}
              >
                <IconComponent className="w-3 h-3 mr-1" />
                {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days left`}
              </Badge>
            </div>
            <p className={cn('text-sm', urgency === 'normal' ? 'text-amber-700' : 'text-white/90')}>
              {getMessage()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            size="sm"
            data-testid="founder-upgrade-btn"
            className={cn(
              urgency === 'normal'
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-white text-amber-700 hover:bg-amber-50'
            )}
            onClick={() => navigate('/founders-pricing')}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {daysRemaining <= 0 ? 'Upgrade Now' : 'Lock In Pricing'}
          </Button>
          
          {urgency === 'normal' && (
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-amber-100 rounded text-amber-600"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Progress bar for remaining days */}
      {daysRemaining > 0 && (
        <div className="mt-3">
          <div className={cn(
            'h-1.5 rounded-full overflow-hidden',
            urgency === 'normal' ? 'bg-amber-200' : 'bg-white/30'
          )}>
            <div 
              className={cn(
                'h-full rounded-full transition-all duration-500',
                urgency === 'normal' ? 'bg-amber-500' : 'bg-white'
              )}
              style={{ width: `${Math.min(100, (daysRemaining / 60) * 100)}%` }}
            />
          </div>
          <p className={cn(
            'text-xs mt-1',
            urgency === 'normal' ? 'text-amber-600' : 'text-white/70'
          )}>
            {Math.round((daysRemaining / 60) * 100)}% of trial remaining
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for sidebar or smaller spaces
 */
export function FounderTrialBadgeCompact({ className }) {
  const navigate = useNavigate();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrialInfo = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/access/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.is_founder && data.founder_status !== 'paid') {
            setDaysRemaining(data.founder_days_remaining);
          }
        }
      } catch (error) {
        console.error('Error fetching trial info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrialInfo();
  }, []);

  if (loading || daysRemaining === null) return null;

  const isUrgent = daysRemaining <= 7;

  return (
    <div 
      data-testid="founder-trial-badge-compact"
      className={cn(
        'p-3 rounded-lg cursor-pointer transition-all',
        isUrgent 
          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600' 
          : 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 hover:from-amber-200 hover:to-orange-200',
        className
      )}
      onClick={() => navigate('/founders-pricing')}
    >
      <div className="flex items-center gap-2 mb-1">
        <Crown className="w-4 h-4" />
        <span className="font-semibold text-sm">
          {daysRemaining <= 0 ? 'Trial Ended' : `${daysRemaining} Days Left`}
        </span>
      </div>
      <p className={cn('text-xs', isUrgent ? 'text-white/90' : 'text-amber-700')}>
        {daysRemaining <= 0 
          ? 'Lock in your Founder pricing now' 
          : 'Founder Circle Trial'
        }
      </p>
    </div>
  );
}

export default FounderTrialBadge;
